import React from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import type { ThemeData, ThemeWithOwnership, BoardTheme } from '../../types';
import { DEFAULT_THEME_DATA, THEME_CATEGORIES } from '../../types';
import { ThemeWrapper } from './ThemeWrapper';

interface ThemeFullPreviewProps {
  /** The theme to preview */
  theme: ThemeWithOwnership | BoardTheme;
  /** Whether the dialog is open */
  open: boolean;
  /** Handler to close the dialog */
  onClose: () => void;
  /** Optional action buttons to show in the dialog footer */
  actions?: React.ReactNode;
}

// Sample numbers matching actual board layout
const SAMPLE_NUMBERS = [0, 3, 7, 1, 9, 4, 2, 6, 8, 5];

// Generate sample cell states for a realistic preview
const generateCellStates = (): ('unclaimed' | 'claimed_self' | 'claimed_other' | 'winner' | 'offline')[][] => {
  const states: ('unclaimed' | 'claimed_self' | 'claimed_other' | 'winner' | 'offline')[][] = [];
  for (let row = 0; row < 10; row++) {
    const rowStates: ('unclaimed' | 'claimed_self' | 'claimed_other' | 'winner' | 'offline')[] = [];
    for (let col = 0; col < 10; col++) {
      const seed = row * 10 + col;
      // Winner squares (sparse)
      if ((row === 2 && col === 4) || (row === 5 && col === 7) || (row === 8 && col === 1)) {
        rowStates.push('winner');
      }
      // Self claimed squares (scattered)
      else if (seed % 7 === 0) {
        rowStates.push('claimed_self');
      }
      // Offline player squares (rare)
      else if (seed % 11 === 0) {
        rowStates.push('offline');
      }
      // Other claimed squares (many)
      else if (seed % 3 !== 0) {
        rowStates.push('claimed_other');
      }
      // Unclaimed
      else {
        rowStates.push('unclaimed');
      }
    }
    states.push(rowStates);
  }
  return states;
};

const CELL_STATES = generateCellStates();

// Get color for a cell based on state
const getCellColor = (state: string, themeData: ThemeData): string => {
  switch (state) {
    case 'winner':
      return themeData.squareWinner;
    case 'claimed_self':
      return themeData.squareClaimedSelf;
    case 'claimed_other':
      return themeData.squareClaimedOther;
    case 'offline':
      return themeData.squareOfflinePlayer;
    default:
      return themeData.squareUnclaimed;
  }
};

// Get category display label
const getCategoryLabel = (category: string): string => {
  const found = THEME_CATEGORIES.find((c) => c.value === category);
  return found?.label || category;
};

// Grid dimensions matching SquaresGrid
const CELL_SIZE = 50;
const HEADER_SIZE = 40;
const GAP = 4;

/**
 * ThemeFullPreview - A full-page preview dialog showing how a theme looks on an actual board
 * Uses the same layout structure as SquaresGrid for accurate representation
 */
export const ThemeFullPreview: React.FC<ThemeFullPreviewProps> = ({
  theme,
  open,
  onClose,
  actions,
}) => {
  const themeData = theme.themeData || DEFAULT_THEME_DATA;
  const gridHeight = CELL_SIZE * 10 + GAP * 9;

  // Sample team data
  const topTeam = 'Chiefs';
  const leftTeam = 'Eagles';
  const topColor = themeData.homeTeamColor || '#1976d2';
  const leftColor = themeData.awayTeamColor || '#d32f2f';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6">{theme.name}</Typography>
          <Chip label={getCategoryLabel(theme.category)} size="small" variant="outlined" />
          {theme.isFree && <Chip label="Free" size="small" color="success" />}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, overflow: 'auto' }}>
        {/* Full page preview with ThemeWrapper */}
        <ThemeWrapper themeData={themeData} showAnimations={true}>
          <Box sx={{ p: 3, minHeight: 500, position: 'relative' }}>
            {/* PREVIEW Watermark */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
                zIndex: 10,
                overflow: 'hidden',
              }}
            >
              <Typography
                sx={{
                  fontSize: '120px',
                  fontWeight: 900,
                  color: 'rgba(128, 128, 128, 0.25)',
                  transform: 'rotate(-35deg)',
                  whiteSpace: 'nowrap',
                  userSelect: 'none',
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                }}
              >
                PREVIEW
              </Typography>
            </Box>
            {/* Board Grid - matching SquaresGrid layout exactly */}
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box sx={{ display: 'inline-block', minWidth: 'fit-content' }}>
                {/* Top Team Name - above the numbers */}
                <Box sx={{ display: 'flex', mb: 1 }}>
                  {/* Spacer for left team label and numbers column */}
                  <Box sx={{ width: HEADER_SIZE + HEADER_SIZE }} />
                  <Typography
                    variant="h6"
                    sx={{
                      width: CELL_SIZE * 10 + GAP * 9 + GAP + 8,
                      textAlign: 'center',
                      fontWeight: 'bold',
                      color: topColor,
                      fontSize: '1.1rem',
                      fontFamily: themeData.fontFamily || 'inherit',
                    }}
                  >
                    {topTeam} (Home)
                  </Typography>
                </Box>

                {/* Top Numbers Row */}
                <Box sx={{ display: 'flex', mb: 0.5 }}>
                  {/* Spacer for left team label and numbers */}
                  <Box sx={{ width: HEADER_SIZE }} />
                  <Box sx={{ width: HEADER_SIZE }} />
                  {/* Top team numbers */}
                  {SAMPLE_NUMBERS.map((num, col) => (
                    <Box
                      key={col}
                      sx={{
                        width: CELL_SIZE,
                        height: HEADER_SIZE,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: themeData.headerBackground || '#fafafa',
                        border: `1px solid ${themeData.cellBorder || '#e0e0e0'}`,
                        color: topColor,
                        fontWeight: themeData.headerFontWeight || '600',
                        fontFamily: themeData.fontFamily || 'inherit',
                        ml: 0.5,
                      }}
                    >
                      {num}
                    </Box>
                  ))}
                </Box>

                {/* Main Grid with Left Team */}
                <Box sx={{ display: 'flex' }}>
                  {/* Left Team Label - vertically centered */}
                  <Box
                    sx={{
                      width: HEADER_SIZE,
                      height: gridHeight,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        writingMode: 'vertical-rl',
                        transform: 'rotate(180deg)',
                        fontWeight: 'bold',
                        color: leftColor,
                        whiteSpace: 'nowrap',
                        fontSize: '1.1rem',
                        fontFamily: themeData.fontFamily || 'inherit',
                      }}
                    >
                      {leftTeam} (Away)
                    </Typography>
                  </Box>

                  {/* Left Numbers Column */}
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {SAMPLE_NUMBERS.map((num, row) => (
                      <Box
                        key={row}
                        sx={{
                          width: HEADER_SIZE,
                          height: CELL_SIZE,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: themeData.headerBackground || '#fafafa',
                          border: `1px solid ${themeData.cellBorder || '#e0e0e0'}`,
                          color: leftColor,
                          fontWeight: themeData.headerFontWeight || '600',
                          fontFamily: themeData.fontFamily || 'inherit',
                          mb: 0.5,
                        }}
                      >
                        {num}
                      </Box>
                    ))}
                  </Box>

                  {/* Grid Cells */}
                  <Box>
                    {CELL_STATES.map((row, rowIndex) => (
                      <Box key={rowIndex} sx={{ display: 'flex', mb: 0.5 }}>
                        {row.map((state, colIndex) => {
                          const isWinner = state === 'winner';
                          const bgColor = getCellColor(state, themeData);
                          const squareNumber = rowIndex * 10 + colIndex + 1;

                          return (
                            <Box
                              key={colIndex}
                              sx={{
                                width: CELL_SIZE,
                                height: CELL_SIZE,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: bgColor,
                                border: `1px solid ${themeData.cellBorder || '#e0e0e0'}`,
                                borderRadius: `${themeData.cellBorderRadius || 0}px`,
                                ml: 0.5,
                                position: 'relative',
                                cursor: 'default',
                              }}
                            >
                              {isWinner ? (
                                <Box
                                  sx={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: '50%',
                                    bgcolor: themeData.winnerBadgeBackground || '#ffd700',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '0.7rem',
                                    fontWeight: 'bold',
                                    color: themeData.winnerBadgeTextColor || '#000',
                                  }}
                                >
                                  ★
                                </Box>
                              ) : state === 'unclaimed' ? (
                                <Typography
                                  sx={{
                                    fontSize: themeData.cellFontSize || '0.75rem',
                                    fontWeight: themeData.cellFontWeight || '500',
                                    fontFamily: themeData.fontFamily || 'inherit',
                                    color: 'text.secondary',
                                  }}
                                >
                                  {squareNumber}
                                </Typography>
                              ) : (
                                <Typography
                                  sx={{
                                    fontSize: '0.65rem',
                                    fontWeight: '600',
                                    fontFamily: themeData.fontFamily || 'inherit',
                                    color: 'text.primary',
                                  }}
                                >
                                  {state === 'claimed_self' ? 'YOU' : state === 'offline' ? 'OFF' : 'ABC'}
                                </Typography>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* Legend */}
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap', mt: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        bgcolor: themeData.squareUnclaimed,
                        border: `1px solid ${themeData.cellBorder || '#ccc'}`,
                        borderRadius: `${themeData.cellBorderRadius || 0}px`,
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ color: themeData.textColorMode === 'light' ? '#fff' : 'text.secondary' }}
                    >
                      Available
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        bgcolor: themeData.squareClaimedSelf,
                        border: `1px solid ${themeData.cellBorder || '#ccc'}`,
                        borderRadius: `${themeData.cellBorderRadius || 0}px`,
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ color: themeData.textColorMode === 'light' ? '#fff' : 'text.secondary' }}
                    >
                      Your Squares
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        bgcolor: themeData.squareClaimedOther,
                        border: `1px solid ${themeData.cellBorder || '#ccc'}`,
                        borderRadius: `${themeData.cellBorderRadius || 0}px`,
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ color: themeData.textColorMode === 'light' ? '#fff' : 'text.secondary' }}
                    >
                      Claimed
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        bgcolor: themeData.squareOfflinePlayer,
                        border: `1px solid ${themeData.cellBorder || '#ccc'}`,
                        borderRadius: `${themeData.cellBorderRadius || 0}px`,
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ color: themeData.textColorMode === 'light' ? '#fff' : 'text.secondary' }}
                    >
                      Offline
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        bgcolor: themeData.squareWinner,
                        border: `1px solid ${themeData.cellBorder || '#ccc'}`,
                        borderRadius: `${themeData.cellBorderRadius || 0}px`,
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ color: themeData.textColorMode === 'light' ? '#fff' : 'text.secondary' }}
                    >
                      Winner
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </ThemeWrapper>

        {/* Theme Info */}
        {theme.description && (
          <Box sx={{ p: 2, bgcolor: 'grey.50', borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">
              {theme.description}
            </Typography>
          </Box>
        )}
      </DialogContent>
      {actions && <DialogActions>{actions}</DialogActions>}
    </Dialog>
  );
};

export default ThemeFullPreview;
